import { useMemo } from 'react';

import { useMy } from '@/hooks/useMy';
import { useSeeds } from '@/hooks/useSeeds';
import { SeedFilter } from '@/hooks/useSeeds';
import { Seed } from '@/types';
import { useMergeStatus } from '@/utils/api';

/**
 * Coordinated seed data fetching hook
 *
 * Manages parallel data fetching for the Seeds section with unified state:
 * - Seeds data (domain/ip/webapplication) with pagination
 * - Seed counts by type (tld, domain, cidr, ipv4, ipv6, webapplication)
 * - Preseed count for the preseed tab badge
 *
 * Performance strategy:
 * - All queries run in parallel via React Query's automatic batching
 * - Loading states unified to prevent UI flicker during transitions
 * - Error states consolidated for consistent error handling
 * - Refetch coordinated across all queries
 *
 * @param filter - Current filter state including type, search, sort, and advancedFilters
 * @returns Coordinated data, loading states, errors, and refetch utilities
 *
 * @example
 * ```tsx
 * const {
 *   seeds,
 *   counts,
 *   preseedCount,
 *   isLoading,
 *   error,
 *   refetch
 * } = useSeedData(filter);
 *
 * // Use unified loading state
 * if (isLoading) return <Loader />;
 *
 * // Use counts for tab headers
 * <SeedsHeader counts={counts} preseedCount={preseedCount} />
 *
 * // Use seeds data for table
 * <SeedsTable data={seeds} />
 * ```
 */
export const useSeedData = (filter: SeedFilter) => {
  const { type, search, sort, advancedFilters } = filter;

  // 1. Fetch seeds data (for all types including preseed)
  const seedsQuery = useSeeds({
    type,
    search,
    sort,
    advancedFilters,
  });

  // 2. Fetch seed counts (for tab badges)
  const countsQuery = useMy({
    resource: 'statistic',
    query: '#class##seed##now#',
  });

  // 3. Fetch preseed count (for preseed tab badge)
  const preseedCountQuery = useMy({
    resource: 'statistic',
    query: '#class##preseed##now#',
  });

  // 4. Compute seed counts from statistics response
  const counts = useMemo(() => {
    const resp = countsQuery.data || [];

    const getCount = (value: string) =>
      resp.find(item => item.value === value)?.data?.count || 0;

    return {
      tld: getCount('tld'),
      domain: getCount('domain'),
      cidr: getCount('cidr'),
      ipv4: getCount('ipv4'),
      ipv6: getCount('ipv6'),
      webapplication: getCount('webapplication'),
    };
  }, [countsQuery.data]);

  // 5. Extract preseed count from statistics response
  const preseedCount = preseedCountQuery.data?.[0]?.data?.count || 0;

  // 6. Calculate total count for current seed type
  // This provides the total database count for the selection bar
  const totalCount = useMemo(() => {
    switch (type) {
      case 'domain':
        return counts.tld + counts.domain;
      case 'ip':
        return counts.cidr + counts.ipv4 + counts.ipv6;
      case 'webapplication':
        return counts.webapplication;
      case 'preseed':
        return preseedCount;
      default:
        return 0;
    }
  }, [type, counts, preseedCount]);

  // 6b. Compute global account empty state across ALL seed types
  // Used for initial loading state - show spinner for empty accounts, tabs for accounts with data
  const isAccountEmpty = useMemo(() => {
    // Can only determine after counts are loaded
    if (
      countsQuery.status !== 'success' ||
      preseedCountQuery.status !== 'success'
    ) {
      return undefined; // Unknown - still loading
    }

    // Sum all seed type counts
    const globalTotalCount =
      counts.tld +
      counts.domain +
      counts.cidr +
      counts.ipv4 +
      counts.ipv6 +
      counts.webapplication +
      preseedCount;

    return globalTotalCount === 0;
  }, [counts, preseedCount, countsQuery.status, preseedCountQuery.status]);

  // 7. Merge loading states (unified loading experience)
  const isLoading = useMergeStatus(
    seedsQuery.status,
    countsQuery.status,
    preseedCountQuery.status
  );

  // 8. Consolidate error states (first error takes precedence)
  const error =
    seedsQuery.error || countsQuery.error || preseedCountQuery.error || null;

  // 9. Coordinated refetch function
  const refetch = () => {
    seedsQuery.refetch();
    countsQuery.refetch();
    preseedCountQuery.refetch();
  };

  return {
    // Data
    seeds: (seedsQuery.data || []) as Seed[], // Preseed[] also compatible with Seed[]
    counts,
    preseedCount,
    totalCount, // Pre-computed total for current seed type

    // Seeds query-specific properties (for table pagination, empty states, etc.)
    fetchNextPage: seedsQuery.fetchNextPage,
    isFetching: seedsQuery.isFetching,
    isAllFetching: seedsQuery.isAllFetching,
    isFetchingNextPage: seedsQuery.isFetchingNextPage,
    hasNextPage: seedsQuery.hasNextPage,
    isAllEmpty: seedsQuery.isAllEmpty,
    isFilteredEmpty: seedsQuery.isFilteredEmpty,
    status: seedsQuery.status,

    // Unified loading states
    isLoading: isLoading === 'pending',
    isSeedsLoading: seedsQuery.status === 'pending',
    isCountsLoading: countsQuery.status === 'pending',
    isPreseedCountLoading: preseedCountQuery.status === 'pending',

    // Global account state
    isAccountEmpty,

    // Error states
    error,
    seedsError: seedsQuery.error || null,
    countsError: countsQuery.error || null,
    preseedCountError: preseedCountQuery.error || null,

    // Utilities
    refetch,
    refetchSeeds: seedsQuery.refetch,
    refetchCounts: countsQuery.refetch,
    refetchPreseedCount: preseedCountQuery.refetch,
  };
};

/**
 * Return type for useSeedData hook
 */
export interface UseSeedDataReturn {
  // Data
  seeds: Seed[];
  counts: {
    tld: number;
    domain: number;
    cidr: number;
    ipv4: number;
    ipv6: number;
    webapplication: number;
  };
  preseedCount: number;
  totalCount: number; // Total count for current seed type

  // Seeds query-specific properties
  fetchNextPage: () => void;
  isFetching: boolean;
  isAllFetching: boolean;
  isFetchingNextPage: boolean;
  hasNextPage: boolean | undefined;
  isAllEmpty: boolean;
  isFilteredEmpty: boolean;
  status: 'pending' | 'error' | 'success';

  // Unified loading states
  isLoading: boolean;
  isSeedsLoading: boolean;
  isCountsLoading: boolean;
  isPreseedCountLoading: boolean;

  /**
   * Global account empty state across ALL seed types.
   * - undefined: Still loading counts
   * - true: Account has zero seeds of any type
   * - false: Account has at least one seed
   */
  isAccountEmpty: boolean | undefined;

  // Error states
  error: Error | null;
  seedsError: Error | null;
  countsError: Error | null;
  preseedCountError: Error | null;

  // Utilities
  refetch: () => void;
  refetchSeeds: () => void;
  refetchCounts: () => void;
  refetchPreseedCount: () => void;
}
